import { useCallback, useEffect, useState } from "react";
import {
  Archive,
  CalendarDays,
  ClipboardCheck,
  Download,
  FileBadge,
  FileText,
  FlaskConical,
  LayoutDashboard,
  Plus,
  Search,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import { api } from "./api";
import { CertificationsModule } from "./certifications";
import { KanbanBoard } from "./kanban-board";
import {useCertificationWorkflowKanban,useWorkflowKanbanColumns,workflowColumnForRow} from "./certification-workflow-kanban";
import {
  MultiSelectFilter,
  TableDisplayControls,
  useTablePreferences,
} from "./table-controls";
import { AttachmentFullPreview } from "./attachment-thumbnail";
import { DatePicker } from "./date-picker";
import {
  ATTACHMENT_ACCEPT,
  attachmentSelectionError,
} from "./attachment-policy";
import { CertificationEntityResourcesPanel } from "./certification-entity-resources";
import {
  CertificationAttachmentPicker,
  fileToBase64,
  uploadCertificationAttachments,
} from "./certification-attachment-picker";

type Tab =
  | "overview"
  | "certifications"
  | "audits"
  | "tests"
  | "requests"
  | "documents";
const tabs: Array<{ id: Tab; label: string; icon: any }> = [
  { id: "overview", label: "Visão geral", icon: LayoutDashboard },
  { id: "certifications", label: "Certificações", icon: FileBadge },
  { id: "audits", label: "Auditorias", icon: ClipboardCheck },
  { id: "tests", label: "Ensaios", icon: FlaskConical },
  { id: "requests", label: "Solicitações", icon: ShieldCheck },
  { id: "documents", label: "Documentos", icon: FileText },
];
const date = (value?: string) =>
  value
    ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR")
    : "Não informado";
const money = (value = 0) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(value) || 0,
  );
const val = (form: FormData, name: string) => String(form.get(name) || "");
const uniqueValues = (items: any[], key: string) =>
  Array.from(
    new Set(items.map((item) => String(item[key] || "").trim()).filter(Boolean)),
  ).sort((left, right) => left.localeCompare(right, "pt-BR"));
const auditColumns = [
  { key: "code", label: "Código", required: true },
  { key: "scope", label: "Escopo" },
  { key: "type", label: "Tipo" },
  { key: "auditor", label: "Auditor" },
  { key: "planned", label: "Planejada" },
  { key: "findings", label: "Constatações" },
  { key: "status", label: "Situação" },
];
const requestColumns = [
  { key: "code", label: "Código", required: true },
  { key: "certification", label: "Certificação ou selo" },
  { key: "type", label: "Tipo" },
  { key: "requester", label: "Solicitante" },
  { key: "priority", label: "Prioridade" },
  { key: "date", label: "Data" },
  { key: "status", label: "Situação" },
];
const testColumns = [
  { key: "code", label: "Código", required: true },
  { key: "product", label: "Produto" },
  { key: "test", label: "Ensaio" },
  { key: "laboratory", label: "Laboratório" },
  { key: "preparation", label: "Preparação antecipada" },
  { key: "result", label: "Resultado" },
  { key: "status", label: "Situação" },
  { key: "cost", label: "Custo" },
];
const documentColumns = [
  { key: "title", label: "Título", required: true },
  { key: "type", label: "Tipo" },
  { key: "certification", label: "Certificação" },
  { key: "number", label: "Número" },
  { key: "issued", label: "Emissão" },
  { key: "expires", label: "Validade" },
  { key: "alert", label: "Alerta" },
  { key: "owner", label: "Responsável" },
  { key: "status", label: "Situação" },
];
export function CertificationHub({
  onError,
}: {
  onError: (message: string) => void;
}) {
  const [tab, setTab] = useState<Tab>("overview");
  return (
    <section className="certification-hub">
      <nav
        className="certification-tabs"
        aria-label="Áreas de selos e certificações"
      >
        {tabs.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              className={tab === item.id ? "active" : ""}
              onClick={() => setTab(item.id)}
            >
              <Icon />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
      {tab === "overview" && <Overview onError={onError} />}
      {tab === "certifications" && <CertificationsModule onError={onError} />}
      {tab === "audits" && <Audits onError={onError} />}
      {tab === "tests" && <Tests onError={onError} />}
      {tab === "requests" && <Requests onError={onError} />}
      {tab === "documents" && <Documents onError={onError} />}
    </section>
  );
}

function Overview({ onError }: { onError: (x: string) => void }) {
  const [data, setData] = useState<any>();
  useEffect(() => {
    api
      .certificationOverview()
      .then(setData)
      .catch((error) => onError(error.message));
  }, [onError]);
  if (!data)
    return (
      <section className="card loading-card">Carregando visão geral…</section>
    );
  const c = data.cards || {};
  return (
    <div className="certification-overview">
      <div className="certification-overview-columns">
        <section className="card certification-agenda">
          <header>
            <div>
              <span className="eyebrow">Agenda</span>
              <h2>Próximos compromissos</h2>
            </div>
            <CalendarDays />
          </header>
          {data.agenda?.length ? (
            <div className="agenda-list">
              {data.agenda.map((item: any) => (
                <article key={`${item.kind}-${item.id}`}>
                  <span className="status-pill info">{item.kind}</span>
                  <div>
                    <b>{item.title}</b>
                    <small>
                      {item.code} · {date(item.event_date)}
                    </small>
                  </div>
                  <span>{item.status}</span>
                </article>
              ))}
            </div>
          ) : (
            <Empty text="Nenhum compromisso programado." />
          )}
        </section>
        <section className="card certification-cost-overview">
          <span className="eyebrow">Custos no ano</span>
          <h2>{money(c.actual)}</h2>
          <p>Realizado de {money(c.planned)} planejados.</p>
          <div className="cost-progress">
            <i
              style={{
                width: `${Math.min(100, c.planned ? (c.actual / c.planned) * 100 : 0)}%`,
              }}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function Audits({ onError }: { onError: (x: string) => void }) {
  const [data, setData] = useState<any>({ items: [] }),
    [query, setQuery] = useState(""),
    [statuses, setStatuses] = useState<string[]>([]),
    [types, setTypes] = useState<string[]>([]),
    [selected, setSelected] = useState<any>(),
    [dialog, setDialog] = useState<"create" | "finding" | null>(null),
    [attachments, setAttachments] = useState<File[]>([]),
    [findingAttachments, setFindingAttachments] = useState<File[]>([]),
    [savingAudit, setSavingAudit] = useState(false),
    [savingFinding, setSavingFinding] = useState(false);
  const workflowTemplate=useCertificationWorkflowKanban("audits",onError);
  const workflowColumns=useWorkflowKanbanColumns(workflowTemplate);
  const preferences = useTablePreferences(
    "sgq:certification-audits-table",
    auditColumns,
    "table",
  );
  const load = useCallback(
    () =>
      api
        .internalAudits(query)
        .then(setData)
        .catch((e) => onError(e.message)),
    [query, onError],
  );
  useEffect(() => {
    const t = setTimeout(load, 180);
    return () => clearTimeout(t);
  }, [load]);
  const open = async (id: string) => {
    try {
      setSelected(await api.internalAudit(id));
    } catch (e) {
      onError((e as Error).message);
    }
  };
  const submitAudit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setSavingAudit(true);
    try {
      const created = await api.createInternalAudit({
        type: val(f, "type"),
        scope: val(f, "scope"),
        auditedUnit: val(f, "auditedUnit"),
        auditor: val(f, "auditor"),
        participants: val(f, "responsible"),
        plannedAt: val(f, "plannedAt"),
        status: "PLANEJADA",
      });
      const cost = Number(val(f, "cost") || 0);
      if (cost > 0) {
        try {
          await api.addCertificationEntityCost("internal_audit", created.id, {
            type: "AUDITORIA",
            description: "CUSTO INICIAL DA AUDITORIA",
            costDate: val(f, "plannedAt"),
            plannedValue: cost,
            actualValue: cost,
          });
        } catch {
          onError(
            "A auditoria foi criada, mas não foi possível registrar o custo. Você pode adicioná-lo ao abrir a auditoria.",
          );
        }
      }
      try {
        await uploadCertificationAttachments(
          "internal_audit",
          created.id,
          attachments,
        );
      } catch {
        onError(
          "A auditoria foi criada, mas não foi possível enviar todos os anexos. Você pode adicioná-los ao abrir a auditoria.",
        );
      }
      setAttachments([]);
      setDialog(null);
      load();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSavingAudit(false);
    }
  };
  const addFinding = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setSavingFinding(true);
    try {
      await api.addAuditFinding(selected.audit.id, {
        title: val(f, "title"),
        description: val(f, "description"),
        evidence: val(f, "evidence"),
        mandatory: true,
      });
      try {
        await uploadCertificationAttachments(
          "internal_audit",
          selected.audit.id,
          findingAttachments,
        );
      } catch {
        onError(
          "A constatação foi criada, mas não foi possível enviar todos os anexos. Você pode adicioná-los na área de anexos da auditoria.",
        );
      }
      setFindingAttachments([]);
      setDialog(null);
      await open(selected.audit.id);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSavingFinding(false);
    }
  };
  if (selected) {
    const findings = selected.findings || [];
    return (
      <div className="workflow-detail">
        <button className="button quiet" onClick={() => setSelected(null)}>
          ← Voltar para auditorias
        </button>
        <section className="card workflow-heading">
          <div>
            <span className="eyebrow">{selected.audit.code}</span>
            <h2>{selected.audit.scope}</h2>
            <p>
              {selected.audit.type} · {selected.audit.auditor} ·{" "}
              {date(selected.audit.planned_at)}
            </p>
          </div>
          <span className="status-pill info">{selected.audit.status}</span>
        </section>
        <CertificationEntityResourcesPanel
          entityType="internal_audit"
          entityId={selected.audit.id}
          recordName={selected.audit.scope}
          onDeleted={() => {
            setSelected(null);
            load();
          }}
          onError={onError}
        />
        <section className="card workflow-section">
          <div className="section-heading">
            <div>
              <h3>Constatações e não conformidades</h3>
              <p>Selecione constatações para gerar NCs no módulo existente.</p>
            </div>
            <button
              className="button"
              onClick={() => {
                setFindingAttachments([]);
                setDialog("finding");
              }}
            >
              <Plus />
              Nova constatação
            </button>
          </div>
          {findings.length ? (
            <div className="finding-list">
              {findings.map((f: any) => (
                <article key={f.id}>
                  <label>
                    <input
                      type="checkbox"
                      value={f.id}
                      disabled={!!f.occurrence_code}
                    />
                    <div>
                      <b>{f.title}</b>
                      <p>{f.description}</p>
                      <small>
                        {f.occurrence_code
                          ? `NC ${f.occurrence_code} · ${f.occurrence_status}`
                          : f.resolved_at
                            ? "Resolvida"
                            : "Pendente"}
                      </small>
                    </div>
                  </label>
                  {!f.resolved_at && (
                    <button
                      className="button quiet"
                      onClick={async () => {
                        await api.resolveAuditFinding(selected.audit.id, f.id);
                        open(selected.audit.id);
                      }}
                    >
                      Resolver
                    </button>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <Empty text="Nenhuma constatação registrada." />
          )}
          <button
            className="button primary"
            disabled={!findings.some((f: any) => !f.occurrence_code)}
            onClick={async () => {
              const ids = Array.from(
                document.querySelectorAll<HTMLInputElement>(
                  ".finding-list input:checked",
                ),
              ).map((x) => x.value);
              if (!ids.length)
                return onError(
                  "Selecione ao menos uma constatação para gerar as NCs.",
                );
              try {
                await api.generateAuditOccurrences(selected.audit.id, ids);
                await open(selected.audit.id);
              } catch (e) {
                onError((e as Error).message);
              }
            }}
          >
            Gerar NCs selecionadas
          </button>
        </section>
        {dialog === "finding" && (
          <Modal
            title="Nova constatação"
            onClose={() => {
              if (!savingFinding) {
                setFindingAttachments([]);
                setDialog(null);
              }
            }}
          >
            <form
              className="modal-form compact-modal-form"
              onSubmit={addFinding}
            >
              <div className="grid">
                <Field label="Título" span={4}>
                  <input name="title" required />
                </Field>
                <Field label="Descrição" span={2}>
                  <textarea name="description" rows={2} required />
                </Field>
                <Field label="Evidência" span={2}>
                  <textarea name="evidence" rows={2} />
                </Field>
                <CertificationAttachmentPicker
                  files={findingAttachments}
                  onChange={setFindingAttachments}
                  onError={onError}
                />
              </div>
              <Actions saving={savingFinding} />
            </form>
          </Modal>
        )}
      </div>
    );
  }
  if(!workflowTemplate)return <section className="card loading-card">Carregando modelo de auditorias…</section>;
  const modelColumnFor=(item:any)=>workflowColumnForRow(workflowTemplate,item);
  const modeledItems=data.items.filter((item:any)=>modelColumnFor(item));
  const visibleItems = modeledItems.filter(
    (item: any) => (!statuses.length||statuses.includes(modelColumnFor(item)!.id))&&(!types.length || types.includes(item.type)),
  );
  return (
    <ModuleList
      title="Auditorias internas"
      query={query}
      onQuery={setQuery}
      onAdd={() => {
        setAttachments([]);
        setDialog("create");
      }}
      addLabel="Nova auditoria"
      className={preferences.className}
      view={preferences.view}
      filters={
        <>
          <MultiSelectFilter
            label="Todos os status"
            options={workflowTemplate.columns.map(column => ({
              value: column.id,
              label: column.name,
            }))}
            value={statuses}
            onChange={setStatuses}
          />
          <MultiSelectFilter
            label="Todos os tipos"
            options={uniqueValues(data.items, "type").map((type) => ({
              value: type,
              label: type,
            }))}
            value={types}
            onChange={setTypes}
          />
        </>
      }
      controls={
        <TableDisplayControls
          count={visibleItems.length}
          total={modeledItems.length}
          columns={auditColumns}
          hidden={preferences.hidden}
          view={preferences.view}
          onView={preferences.setView}
          onToggle={preferences.toggle}
          onReset={preferences.reset}
        />
      }
    >
      {preferences.view === "kanban" ? (
        <KanbanBoard
          columns={workflowColumns}
          rows={visibleItems}
          getStatus={(item: any) => modelColumnFor(item)?.id||""}
          onOpen={(item: any) => void open(item.id)}
          onStatusChange={async(item:any,columnId,comment)=>{try{await api.moveCertificationWorkflowKanbanItem("audits",item.id,columnId,comment);await load()}catch(error:any){onError(error.message);throw error}}}
          renderCard={(item: any) => (
            <>
              <strong>{item.scope}</strong>
              <span className="kanban-card-customer">{item.auditor}</span>
              <span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>
                {modelColumnFor(item)?.name}
              </span>
              <small>
                {item.code} · {date(item.planned_at)}
              </small>
            </>
          )}
        />
      ) : (
      <table>
        <thead>
          <tr>
            <th data-column="code">Código</th>
            <th data-column="scope">Escopo</th>
            <th data-column="type">Tipo</th>
            <th data-column="auditor">Auditor</th>
            <th data-column="planned">Planejada</th>
            <th data-column="findings">Constatações</th>
            <th data-column="status">Situação</th>
          </tr>
        </thead>
        <tbody>
          {visibleItems.map((item: any) => (
            <tr
              key={item.id}
              className="clickable-row"
              tabIndex={0}
              onClick={() => open(item.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  void open(item.id);
                }
              }}
            >
              <td data-column="code" className="mono">{item.code}</td>
              <td data-column="scope">{item.scope}</td>
              <td data-column="type">{item.type}</td>
              <td data-column="auditor">{item.auditor}</td>
              <td data-column="planned">{date(item.planned_at)}</td>
              <td data-column="findings">{item.finding_count}</td>
              <td data-column="status">
                <span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>{modelColumnFor(item)?.name}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
      {!visibleItems.length && <Empty text="Nenhuma auditoria encontrada." />}
      {dialog === "create" && (
        <Modal
          title="Nova auditoria"
          onClose={() => {
            if (!savingAudit) {
              setAttachments([]);
              setDialog(null);
            }
          }}
        >
          <form
            className="modal-form compact-modal-form"
            onSubmit={submitAudit}
          >
            <div className="grid">
              <Field label="Tipo">
                <input
                  name="type"
                  required
                  placeholder="FSC, Selo Verde, Produto…"
                />
              </Field>
              <Field label="Data planejada">
                <DatePicker
                  name="plannedAt"
                  required
                  aria-label="Data planejada"
                />
              </Field>
              <Field label="Auditor responsável">
                <input name="auditor" required />
              </Field>
              <Field label="Setor responsável">
                <input name="auditedUnit" />
              </Field>
              <Field label="Escopo" span={2}>
                <textarea
                  className="audit-scope-fixed"
                  name="scope"
                  rows={2}
                  required
                />
              </Field>
              <Field label="Responsável">
                <input name="responsible" />
              </Field>
              <Field label="Custo">
                <input
                  name="cost"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue="0"
                />
              </Field>
              <CertificationAttachmentPicker
                files={attachments}
                onChange={setAttachments}
                onError={onError}
              />
            </div>
            <Actions saving={savingAudit} />
          </form>
        </Modal>
      )}
    </ModuleList>
  );
}

function Requests({ onError }: { onError: (x: string) => void }) {
  const [data, setData] = useState<any>({ items: [] }),
    [query, setQuery] = useState(""),
    [statuses, setStatuses] = useState<string[]>([]),
    [types, setTypes] = useState<string[]>([]),
    [dialog, setDialog] = useState(false),
    [selected, setSelected] = useState<any>(),
    [saving, setSaving] = useState(false),
    [creatingRequest, setCreatingRequest] = useState(false),
    [attachments, setAttachments] = useState<File[]>([]);
  const workflowTemplate=useCertificationWorkflowKanban("requests",onError);
  const workflowColumns=useWorkflowKanbanColumns(workflowTemplate);
  const preferences = useTablePreferences(
    "sgq:certification-requests-table",
    requestColumns,
    "table",
  );
  const load = useCallback(
    () =>
      api
        .certificationRequests(query)
        .then(setData)
        .catch((e) => onError(e.message)),
    [query, onError],
  );
  useEffect(() => {
    const t = setTimeout(load, 180);
    return () => clearTimeout(t);
  }, [load]);
  const refreshSelected = async (id: string) => {
    const result = await api.certificationRequests(query);
    setData(result);
    setSelected(result.items.find((item: any) => item.id === id));
  };
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setCreatingRequest(true);
    try {
      const created = await api.createCertificationRequest({
        requestedCertification: val(f, "name"),
        type: val(f, "type"),
        requester: val(f, "requester"),
        department: val(f, "department"),
        justification: val(f, "justification"),
        relatedScope: val(f, "scope"),
        priority: val(f, "priority"),
        requestedAt: val(f, "requestedAt"),
        desiredDueAt: val(f, "dueAt"),
        status: "RASCUNHO",
      });
      try {
        await uploadCertificationAttachments(
          "certification_request",
          created.id,
          attachments,
        );
      } catch {
        onError(
          "A solicitação foi criada, mas não foi possível enviar todos os anexos. Você pode adicioná-los ao abrir a solicitação.",
        );
      }
      setAttachments([]);
      setDialog(false);
      await refreshSelected(created.id);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setCreatingRequest(false);
    }
  };
  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await api.updateCertificationRequest(selected.id, {
        requestedCertification: val(f, "name"),
        type: val(f, "type"),
        requester: val(f, "requester"),
        department: val(f, "department"),
        justification: val(f, "justification"),
        relatedScope: val(f, "scope"),
        priority: val(f, "priority"),
        requestedAt: val(f, "requestedAt"),
        desiredDueAt: val(f, "dueAt"),
        responsibleAnalyst: val(f, "analyst"),
        opinion: val(f, "opinion"),
        status: val(f, "status"),
      });
      await refreshSelected(selected.id);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  if (selected)
    return (
      <div className="workflow-detail certification-request-detail">
        <button className="button quiet" onClick={() => setSelected(undefined)}>
          ← Voltar para solicitações
        </button>
        <section className="card workflow-heading">
          <div>
            <span className="eyebrow">{selected.code}</span>
            <h2>{selected.requested_certification}</h2>
            <p>
              {selected.type} · {selected.requester} ·{" "}
              {date(selected.requested_at)}
            </p>
          </div>
          <span className="status-pill info">{selected.status}</span>
        </section>
        <form
          className="card workflow-section certification-request-form"
          key={selected.id}
          onSubmit={save}
        >
          <div className="section-heading">
            <div>
              <h3>Dados da solicitação</h3>
              <p>Atualize o rascunho e encaminhe a demanda para análise.</p>
            </div>
            {selected.converted_certification_code && (
              <span className="status-pill info">
                CERTIFICAÇÃO {selected.converted_certification_code}
              </span>
            )}
          </div>
          <div className="grid">
            <Field label="Certificação ou selo">
              <input
                name="name"
                defaultValue={selected.requested_certification}
                required
              />
            </Field>
            <Field label="Tipo">
              <input name="type" defaultValue={selected.type} required />
            </Field>
            <Field label="Solicitante">
              <input
                name="requester"
                defaultValue={selected.requester}
                required
              />
            </Field>
            <Field label="Setor">
              <input
                name="department"
                defaultValue={selected.department || ""}
              />
            </Field>
            <Field label="Prioridade">
              <select name="priority" defaultValue={selected.priority}>
                <option>BAIXA</option>
                <option>MÉDIA</option>
                <option>ALTA</option>
                <option>CRÍTICA</option>
              </select>
            </Field>
            <Field label="Situação">
              <select name="status" defaultValue={selected.status}>
                {(data.statuses || []).map((status: string) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </Field>
            <Field label="Data da solicitação">
              <DatePicker
                name="requestedAt"
                defaultValue={selected.requested_at?.slice(0, 10) || ""}
                required
                aria-label="Data da solicitação"
              />
            </Field>
            <Field label="Prazo desejado">
              <DatePicker
                name="dueAt"
                defaultValue={selected.desired_due_at?.slice(0, 10) || ""}
                aria-label="Prazo desejado"
              />
            </Field>
            <Field label="Analista responsável">
              <input
                name="analyst"
                defaultValue={selected.responsible_analyst || ""}
              />
            </Field>
            <Field label="Produto, processo ou unidade">
              <input name="scope" defaultValue={selected.related_scope || ""} />
            </Field>
            <Field label="Justificativa" wide>
              <textarea
                name="justification"
                defaultValue={selected.justification}
                required
              />
            </Field>
            <Field label="Parecer" wide>
              <textarea name="opinion" defaultValue={selected.opinion || ""} />
            </Field>
          </div>
          <div className="actions certification-request-actions">
            {selected.status === "APROVADA" &&
              !selected.converted_certification_id && (
                <button
                  className="button"
                  type="button"
                  disabled={saving}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await api.convertCertificationRequest(selected.id);
                      await refreshSelected(selected.id);
                    } catch (e) {
                      onError((e as Error).message);
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Converter em certificação
                </button>
              )}
            <button className="button primary" type="submit" disabled={saving}>
              {saving ? "Salvando…" : "Salvar alterações"}
            </button>
          </div>
        </form>
        <CertificationEntityResourcesPanel
          entityType="certification_request"
          entityId={selected.id}
          recordName={selected.requested_certification}
          onDeleted={() => {
            setSelected(undefined);
            load();
          }}
          onError={onError}
        />
      </div>
    );
  if(!workflowTemplate)return <section className="card loading-card">Carregando modelo de solicitações…</section>;
  const modelColumnFor=(item:any)=>workflowColumnForRow(workflowTemplate,item);
  const modeledItems=data.items.filter((item:any)=>modelColumnFor(item));
  const visibleItems = modeledItems.filter(
    (item: any) =>
      (!statuses.length || statuses.includes(modelColumnFor(item)!.id)) &&
      (!types.length || types.includes(item.type)),
  );
  return (
    <ModuleList
      title="Solicitações de certificação"
      query={query}
      onQuery={setQuery}
      onAdd={() => {
        setAttachments([]);
        setDialog(true);
      }}
      addLabel="Nova solicitação"
      className={preferences.className}
      view={preferences.view}
      filters={
        <>
          <MultiSelectFilter
            label="Todos os status"
            options={workflowTemplate.columns.map(column => ({
              value: column.id,
              label: column.name,
            }))}
            value={statuses}
            onChange={setStatuses}
          />
          <MultiSelectFilter
            label="Todos os tipos"
            options={uniqueValues(data.items, "type").map((type) => ({
              value: type,
              label: type,
            }))}
            value={types}
            onChange={setTypes}
          />
        </>
      }
      controls={
        <TableDisplayControls
          count={visibleItems.length}
          total={modeledItems.length}
          columns={requestColumns}
          hidden={preferences.hidden}
          view={preferences.view}
          onView={preferences.setView}
          onToggle={preferences.toggle}
          onReset={preferences.reset}
        />
      }
    >
      {preferences.view === "kanban" ? (
        <KanbanBoard
          columns={workflowColumns}
          rows={visibleItems}
          getStatus={(item: any) => modelColumnFor(item)?.id||""}
          onOpen={(item: any) => setSelected(item)}
          onStatusChange={async(item:any,columnId,comment)=>{try{await api.moveCertificationWorkflowKanbanItem("requests",item.id,columnId,comment);await load()}catch(error:any){onError(error.message);throw error}}}
          renderCard={(item: any) => (
            <>
              <strong>{item.requested_certification}</strong>
              <span className="kanban-card-customer">{item.requester}</span>
              <span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>
                {modelColumnFor(item)?.name}
              </span>
              <small>
                {item.code} · {item.priority} · {date(item.requested_at)}
              </small>
            </>
          )}
        />
      ) : (
      <table>
        <thead>
          <tr>
            <th data-column="code">Código</th>
            <th data-column="certification">Certificação ou selo</th>
            <th data-column="type">Tipo</th>
            <th data-column="requester">Solicitante</th>
            <th data-column="priority">Prioridade</th>
            <th data-column="date">Data</th>
            <th data-column="status">Situação</th>
          </tr>
        </thead>
        <tbody>
          {visibleItems.map((item: any) => (
            <tr
              key={item.id}
              className="clickable-row"
              tabIndex={0}
              onClick={() => setSelected(item)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSelected(item);
                }
              }}
            >
              <td data-column="code" className="mono">{item.code}</td>
              <td data-column="certification">{item.requested_certification}</td>
              <td data-column="type">{item.type}</td>
              <td data-column="requester">{item.requester}</td>
              <td data-column="priority">{item.priority}</td>
              <td data-column="date">{date(item.requested_at)}</td>
              <td data-column="status">
                <span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>{modelColumnFor(item)?.name}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
      {!visibleItems.length && <Empty text="Nenhuma solicitação encontrada." />}
      {dialog && (
        <Modal
          title="Nova solicitação"
          onClose={() => {
            if (!creatingRequest) {
              setAttachments([]);
              setDialog(false);
            }
          }}
        >
          <form className="modal-form compact-modal-form" onSubmit={submit}>
            <div className="grid">
              <Field label="Certificação ou selo">
                <input name="name" required />
              </Field>
              <Field label="Tipo">
                <input name="type" required />
              </Field>
              <Field label="Solicitante">
                <input name="requester" required />
              </Field>
              <Field label="Setor">
                <input name="department" />
              </Field>
              <Field label="Prioridade">
                <select name="priority">
                  <option>BAIXA</option>
                  <option>MÉDIA</option>
                  <option>ALTA</option>
                  <option>CRÍTICA</option>
                </select>
              </Field>
              <Field label="Data da solicitação">
                <DatePicker
                  name="requestedAt"
                  required
                  aria-label="Data da solicitação"
                />
              </Field>
              <Field label="Prazo desejado">
                <DatePicker name="dueAt" aria-label="Prazo desejado" />
              </Field>
              <Field label="Produto, processo ou unidade">
                <input name="scope" />
              </Field>
              <Field label="Justificativa" wide>
                <textarea name="justification" rows={2} required />
              </Field>
              <CertificationAttachmentPicker
                files={attachments}
                onChange={setAttachments}
                onError={onError}
              />
            </div>
            <Actions saving={creatingRequest} />
          </form>
        </Modal>
      )}
    </ModuleList>
  );
}

function Tests({ onError }: { onError: (x: string) => void }) {
  const [data, setData] = useState<any>({ items: [] }),
    [query, setQuery] = useState(""),
    [statuses, setStatuses] = useState<string[]>([]),
    [types, setTypes] = useState<string[]>([]),
    [dialog, setDialog] = useState(false),
    [selected, setSelected] = useState<any>(),
    [attachments, setAttachments] = useState<File[]>([]),
    [savingTest, setSavingTest] = useState(false);
  const workflowTemplate=useCertificationWorkflowKanban("tests",onError);
  const workflowColumns=useWorkflowKanbanColumns(workflowTemplate);
  const preferences = useTablePreferences(
    "sgq:certification-tests-table",
    testColumns,
    "table",
  );
  const load = useCallback(
    () =>
      api
        .productTests(query)
        .then(setData)
        .catch((e) => onError(e.message)),
    [query, onError],
  );
  useEffect(() => {
    const t = setTimeout(load, 180);
    return () => clearTimeout(t);
  }, [load]);
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setSavingTest(true);
    try {
      const created = await api.createProductTest({
        product: val(f, "product"),
        productCode: val(f, "code"),
        testType: val(f, "type"),
        standard: val(f, "standard"),
        laboratory: val(f, "laboratory"),
        executionType: val(f, "execution"),
        requester: val(f, "requester"),
        owner: val(f, "owner"),
        sample: val(f, "sample"),
        quantity: Number(val(f, "quantity") || 1),
        sentAt: val(f, "sentAt"),
        expectedAt: val(f, "expectedAt"),
        status: "PLANEJADO",
        cost: Number(val(f, "cost") || 0),
        notes: val(f, "notes"),
      });
      try {
        await uploadCertificationAttachments(
          "product_test",
          created.id,
          attachments,
        );
      } catch {
        onError(
          "O ensaio foi criado, mas não foi possível enviar todos os anexos. Você pode adicioná-los ao abrir o ensaio.",
        );
      }
      setAttachments([]);
      setDialog(false);
      load();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSavingTest(false);
    }
  };
  if (selected)
    return (
      <div className="workflow-detail">
        <button className="button quiet" onClick={() => setSelected(undefined)}>
          ← Voltar para ensaios
        </button>
        <section className="card workflow-heading">
          <div>
            <span className="eyebrow">{selected.code}</span>
            <h2>{selected.product}</h2>
            <p>
              {selected.test_type} ·{" "}
              {selected.laboratory || "Laboratório não informado"} ·{" "}
              {date(selected.expected_at)}
            </p>
          </div>
          <span className="status-pill info">{selected.status}</span>
        </section>
        <section className="card workflow-section entity-facts">
          <div>
            <small>Norma</small>
            <b>{selected.standard || "Não informada"}</b>
          </div>
          <div>
            <small>Responsável</small>
            <b>{selected.owner || "Não informado"}</b>
          </div>
          <div>
            <small>Quantidade</small>
            <b>{selected.quantity || 1}</b>
          </div>
          <div>
            <small>Resultado</small>
            <b>{selected.result || "Pendente"}</b>
          </div>
        </section>
        <CertificationEntityResourcesPanel
          entityType="product_test"
          entityId={selected.id}
          recordName={selected.product}
          onDeleted={() => {
            setSelected(undefined);
            load();
          }}
          onError={onError}
        />
      </div>
    );
  if(!workflowTemplate)return <section className="card loading-card">Carregando modelo de ensaios…</section>;
  const modelColumnFor=(item:any)=>workflowColumnForRow(workflowTemplate,item);
  const modeledItems=data.items.filter((item:any)=>modelColumnFor(item));
  const visibleItems = modeledItems.filter(
    (item: any) =>
      (!statuses.length || statuses.includes(modelColumnFor(item)!.id)) &&
      (!types.length || types.includes(item.test_type)),
  );
  return (
    <ModuleList
      title="Ensaios de produtos"
      query={query}
      onQuery={setQuery}
      onAdd={() => {
        setAttachments([]);
        setDialog(true);
      }}
      addLabel="Novo ensaio"
      className={preferences.className}
      view={preferences.view}
      filters={
        <>
          <MultiSelectFilter
            label="Todos os status"
            options={workflowTemplate.columns.map(column => ({
              value: column.id,
              label: column.name,
            }))}
            value={statuses}
            onChange={setStatuses}
          />
          <MultiSelectFilter
            label="Todos os tipos"
            options={uniqueValues(data.items, "test_type").map((type) => ({
              value: type,
              label: type,
            }))}
            value={types}
            onChange={setTypes}
          />
        </>
      }
      controls={
        <TableDisplayControls
          count={visibleItems.length}
          total={modeledItems.length}
          columns={testColumns}
          hidden={preferences.hidden}
          view={preferences.view}
          onView={preferences.setView}
          onToggle={preferences.toggle}
          onReset={preferences.reset}
        />
      }
    >
      {preferences.view === "kanban" ? (
        <KanbanBoard
          columns={workflowColumns}
          rows={visibleItems}
          getStatus={(item: any) => modelColumnFor(item)?.id||""}
          onOpen={(item: any) => setSelected(item)}
          onStatusChange={async(item:any,columnId,comment)=>{try{await api.moveCertificationWorkflowKanbanItem("tests",item.id,columnId,comment);await load()}catch(error:any){onError(error.message);throw error}}}
          renderCard={(item: any) => (
            <>
              <strong>{item.product}</strong>
              <span className="kanban-card-customer">
                {item.test_type} · {item.laboratory || "Laboratório não informado"}
              </span>
              <span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>
                {modelColumnFor(item)?.name}
              </span>
              <small>
                {item.code} · {date(item.expected_at)}
              </small>
            </>
          )}
        />
      ) : (
      <table>
        <thead>
          <tr>
            <th data-column="code">Código</th>
            <th data-column="product">Produto</th>
            <th data-column="test">Ensaio</th>
            <th data-column="laboratory">Laboratório</th>
            <th data-column="preparation">Preparação antecipada</th>
            <th data-column="result">Resultado</th>
            <th data-column="status">Situação</th>
            <th data-column="cost">Custo</th>
          </tr>
        </thead>
        <tbody>
          {visibleItems.map((item: any) => (
            <tr
              key={item.id}
              className="clickable-row"
              tabIndex={0}
              onClick={() => setSelected(item)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSelected(item);
                }
              }}
            >
              <td data-column="code" className="mono">{item.code}</td>
              <td data-column="product">
                {item.product}
                <small className="table-secondary">{item.product_code}</small>
              </td>
              <td data-column="test">
                {item.test_type}
                <small className="table-secondary">{item.standard}</small>
              </td>
              <td data-column="laboratory">{item.laboratory || "Não informado"}</td>
              <td data-column="preparation">{date(item.expected_at)}</td>
              <td data-column="result">{item.result || "Pendente"}</td>
              <td data-column="status">
                <span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>{modelColumnFor(item)?.name}</span>
              </td>
              <td data-column="cost">{money(item.cost)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
      {!visibleItems.length && <Empty text="Nenhum ensaio encontrado." />}
      {dialog && (
        <Modal
          title="Novo ensaio"
          onClose={() => {
            if (!savingTest) {
              setAttachments([]);
              setDialog(false);
            }
          }}
        >
          <form className="modal-form compact-modal-form" onSubmit={submit}>
            <div className="grid">
              <Field label="Produto">
                <input name="product" required />
              </Field>
              <Field label="Código do produto">
                <input name="code" />
              </Field>
              <Field label="Tipo de ensaio">
                <input name="type" required />
              </Field>
              <Field label="Norma">
                <input name="standard" />
              </Field>
              <Field label="Laboratório">
                <input name="laboratory" />
              </Field>
              <Field label="Execução">
                <select name="execution">
                  <option>INTERNA</option>
                  <option>EXTERNA</option>
                </select>
              </Field>
              <Field label="Solicitante">
                <input name="requester" />
              </Field>
              <Field label="Responsável">
                <input name="owner" />
              </Field>
              <Field label="Amostra">
                <input name="sample" />
              </Field>
              <Field label="Quantidade">
                <input name="quantity" type="number" min="1" defaultValue="1" />
              </Field>
              <Field label="Envio">
                <DatePicker name="sentAt" aria-label="Data de envio" />
              </Field>
              <Field label="Preparação antecipada">
                <DatePicker
                  name="expectedAt"
                  aria-label="Preparação antecipada"
                />
              </Field>
              <Field label="Custo">
                <input name="cost" type="number" min="0" step="0.01" />
              </Field>
              <Field label="Observações" span={3}>
                <textarea name="notes" rows={2} />
              </Field>
              <CertificationAttachmentPicker
                files={attachments}
                onChange={setAttachments}
                onError={onError}
              />
            </div>
            <Actions saving={savingTest} />
          </form>
        </Modal>
      )}
    </ModuleList>
  );
}

function Documents({ onError }: { onError: (x: string) => void }) {
  const [data, setData] = useState<any>({ items: [] }),
    [certifications, setCertifications] = useState<any[]>([]),
    [query, setQuery] = useState(""),
    [statuses, setStatuses] = useState<string[]>([]),
    [types, setTypes] = useState<string[]>([]),
    [preview, setPreview] = useState<File>(),
    [downloadUrl, setDownloadUrl] = useState(""),
    [creating, setCreating] = useState(false),
    [saving, setSaving] = useState(false),
    [selectedFileName, setSelectedFileName] = useState(""),
    [attachments, setAttachments] = useState<File[]>([]),
    [selected, setSelected] = useState<any>();
  const workflowTemplate=useCertificationWorkflowKanban("certification_documents",onError);
  const workflowColumns=useWorkflowKanbanColumns(workflowTemplate);
  const preferences = useTablePreferences(
    "sgq:certification-documents-table",
    documentColumns,
    "table",
  );
  const load = useCallback(
    () =>
      api
        .certificationDocuments(query)
        .then(setData)
        .catch((e) => onError(e.message)),
    [query, onError],
  );
  useEffect(() => {
    const t = setTimeout(load, 180);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    api
      .certifications()
      .then((result) => setCertifications(result.items))
      .catch((e) => onError(e.message));
  }, [onError]);
  useEffect(() => {
    if (!preview) {
      setDownloadUrl("");
      return;
    }
    const url = URL.createObjectURL(preview);
    setDownloadUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [preview]);
  const open = async (item: any) => {
    if (!item.has_content) return;
    try {
      const blob = await api.certificationDocumentContent(item.id);
      setPreview(
        new File([blob], item.file_name || item.title, {
          type: item.mime_type || blob.type,
        }),
      );
    } catch (e) {
      onError((e as Error).message);
    }
  };
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget),
      file = form.get("file");
    if (!(file instanceof File) || !file.size)
      return onError("Selecione um arquivo para anexar.");
    const policyError = attachmentSelectionError([file]);
    if (policyError) return onError(policyError);
    setSaving(true);
    try {
      const created = await api.addCertificationDocument(
        val(form, "certificationId"),
        {
        title: val(form, "title"),
        type: val(form, "type"),
        certificateNumber: val(form, "number") || undefined,
        issuedAt: val(form, "issuedAt") || undefined,
        expiresAt: val(form, "expiresAt") || undefined,
        status: val(form, "status"),
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        contentBase64: await fileToBase64(file),
        owner: val(form, "owner") || undefined,
        notes: val(form, "notes") || undefined,
        alertAdvanceValue: val(form, "alertValue")
          ? Number(val(form, "alertValue"))
          : undefined,
        alertAdvanceUnit: val(form, "alertValue")
          ? val(form, "alertUnit")
          : undefined,
        },
      );
      try {
        await uploadCertificationAttachments(
          "certification_document",
          created.id,
          attachments,
        );
      } catch {
        onError(
          "O documento foi criado, mas não foi possível enviar todos os anexos complementares. Você pode adicioná-los ao abrir o documento.",
        );
      }
      setAttachments([]);
      setCreating(false);
      await load();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  if (selected)
    return (
      <div className="workflow-detail">
        <button className="button quiet" onClick={() => setSelected(undefined)}>
          ← Voltar para documentos
        </button>
        <section className="card workflow-heading">
          <div>
            <span className="eyebrow">{selected.type}</span>
            <h2>{selected.title}</h2>
            <p>
              {selected.certification_name} ·{" "}
              {selected.certificate_number ||
                selected.report_number ||
                selected.file_name}
            </p>
          </div>
          <div className="resource-actions">
            <span className="status-pill info">{selected.status}</span>
            {selected.has_content ? (
              <button className="button" onClick={() => open(selected)}>
                <Download />
                Abrir arquivo original
              </button>
            ) : null}
          </div>
        </section>
        <section className="card workflow-section entity-facts">
          <div>
            <small>Emissão</small>
            <b>{date(selected.issued_at)}</b>
          </div>
          <div>
            <small>Validade</small>
            <b>{date(selected.expires_at)}</b>
          </div>
          <div>
            <small>Responsável</small>
            <b>{selected.owner || "Não informado"}</b>
          </div>
          <div>
            <small>Arquivo</small>
            <b>{selected.file_name || "Indisponível"}</b>
          </div>
        </section>
        <CertificationEntityResourcesPanel
          entityType="certification_document"
          entityId={selected.id}
          recordName={selected.title}
          onDeleted={() => {
            setSelected(undefined);
            load();
          }}
          onError={onError}
        />
        {preview && (
          <div
            className="wizard-preview-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setPreview(undefined);
            }}
          >
            <section
              className="wizard-preview-modal"
              role="dialog"
              aria-modal="true"
              aria-label={`Pré-visualização de ${preview.name}`}
            >
              <header>
                <div>
                  <span className="eyebrow">Pré-visualização</span>
                  <h3>{preview.name}</h3>
                  <small>
                    {Math.max(1, Math.round(preview.size / 1024))} KB
                  </small>
                </div>
                <button
                  type="button"
                  onClick={() => setPreview(undefined)}
                  aria-label="Fechar pré-visualização"
                >
                  <X />
                </button>
              </header>
              <div className="wizard-preview-content">
                <AttachmentFullPreview file={preview} />
              </div>
              <footer>
                <a
                  className="button wizard-preview-download"
                  href={downloadUrl}
                  download={preview.name}
                  aria-label="Baixar arquivo original"
                  title="Baixar arquivo original"
                >
                  <Download />
                </a>
              </footer>
            </section>
          </div>
        )}
      </div>
    );
  if(!workflowTemplate)return <section className="card loading-card">Carregando modelo de documentos…</section>;
  const modelColumnFor=(item:any)=>workflowColumnForRow(workflowTemplate,item);
  const modeledItems=data.items.filter((item:any)=>modelColumnFor(item));
  const visibleItems = modeledItems.filter(
    (item: any) =>
      (!statuses.length || statuses.includes(modelColumnFor(item)!.id)) &&
      (!types.length || types.includes(item.type)),
  );
  return (
    <ModuleList
      title="Biblioteca de documentos"
      query={query}
      onQuery={setQuery}
      onAdd={() => {
        setSelectedFileName("");
        setAttachments([]);
        setCreating(true);
      }}
      addLabel="Anexar documento"
      className={preferences.className}
      view={preferences.view}
      filters={
        <>
          <MultiSelectFilter
            label="Todos os status"
            options={workflowTemplate.columns.map(column => ({
              value: column.id,
              label: column.name,
            }))}
            value={statuses}
            onChange={setStatuses}
          />
          <MultiSelectFilter
            label="Todos os tipos"
            options={uniqueValues(data.items, "type").map((type) => ({
              value: type,
              label: type,
            }))}
            value={types}
            onChange={setTypes}
          />
        </>
      }
      controls={
        <TableDisplayControls
          count={visibleItems.length}
          total={modeledItems.length}
          columns={documentColumns}
          hidden={preferences.hidden}
          view={preferences.view}
          onView={preferences.setView}
          onToggle={preferences.toggle}
          onReset={preferences.reset}
        />
      }
    >
      {preferences.view === "kanban" ? (
        <KanbanBoard
          columns={workflowColumns}
          rows={visibleItems}
          getStatus={(item: any) => modelColumnFor(item)?.id||""}
          onOpen={(item: any) => setSelected(item)}
          onStatusChange={async(item:any,columnId,comment)=>{try{await api.moveCertificationWorkflowKanbanItem("certification_documents",item.id,columnId,comment);await load()}catch(error:any){onError(error.message);throw error}}}
          renderCard={(item: any) => (
            <>
              <strong>{item.title}</strong>
              <span className="kanban-card-customer">
                {item.certification_name} · {item.type}
              </span>
              <span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>
                {modelColumnFor(item)?.name}
              </span>
              <small>
                Validade: {date(item.expires_at)} · {item.owner || "Sem responsável"}
              </small>
            </>
          )}
        />
      ) : (
      <table>
        <thead>
          <tr>
            <th data-column="title">Título</th>
            <th data-column="type">Tipo</th>
            <th data-column="certification">Certificação</th>
            <th data-column="number">Número</th>
            <th data-column="issued">Emissão</th>
            <th data-column="expires">Validade</th>
            <th data-column="alert">Alerta</th>
            <th data-column="owner">Responsável</th>
            <th data-column="status">Situação</th>
          </tr>
        </thead>
        <tbody>
          {visibleItems.map((item: any) => (
            <tr
              key={item.id}
              className="clickable-row"
              tabIndex={0}
              onClick={() => setSelected(item)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSelected(item);
                }
              }}
            >
              <td data-column="title">
                {item.title}
                <small className="table-secondary">
                  {item.has_content
                    ? item.file_name
                    : "Arquivo de demonstração indisponível"}
                </small>
              </td>
              <td data-column="type">{item.type}</td>
              <td data-column="certification">{item.certification_name}</td>
              <td data-column="number">
                {item.certificate_number ||
                  item.report_number ||
                  "Não informado"}
              </td>
              <td data-column="issued">{date(item.issued_at)}</td>
              <td data-column="expires">{date(item.expires_at)}</td>
              <td data-column="alert">
                {item.alert_advance_value
                  ? `${item.alert_advance_value} ${item.alert_advance_unit === "months" ? (item.alert_advance_value === 1 ? "mês" : "meses") : item.alert_advance_value === 1 ? "dia" : "dias"}`
                  : "Sem alerta"}
                <small className="table-secondary">
                  {item.alert_at ? `Em ${date(item.alert_at)}` : ""}
                </small>
              </td>
              <td data-column="owner">{item.owner || "Não informado"}</td>
              <td data-column="status"><span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>{modelColumnFor(item)?.name}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
      {!visibleItems.length && <Empty text="Nenhum documento encontrado." />}
      {creating && (
        <Modal
          title="Anexar documento"
          onClose={() => {
            if (!saving) {
              setAttachments([]);
              setCreating(false);
            }
          }}
        >
          <form className="modal-form compact-modal-form" onSubmit={submit}>
            <div className="grid">
              <Field label="Arquivo" wide>
                <label className="document-dropzone certification-document-dropzone">
                  <input
                    className="dropzone-file-input"
                    name="file"
                    type="file"
                    accept={ATTACHMENT_ACCEPT}
                    required
                    onChange={(event) =>
                      setSelectedFileName(
                        event.currentTarget.files?.[0]?.name || "",
                      )
                    }
                  />
                  <Upload />
                  <div>
                    <strong>
                      {selectedFileName || "Selecionar documento"}
                    </strong>
                    <small>
                      {selectedFileName
                        ? "Clique para escolher outro arquivo"
                        : "Compactados, imagens, planilhas, vídeos, áudios ou PDF · até 30 MB"}
                    </small>
                  </div>
                </label>
              </Field>
              <Field label="Título">
                <input name="title" required />
              </Field>
              <Field label="Certificação vinculada">
                <select name="certificationId" required defaultValue="">
                  <option value="" disabled>
                    Selecione
                  </option>
                  {certifications.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} — {item.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Tipo">
                <select name="type">
                  <option>CERTIFICADO</option>
                  <option>LAUDO</option>
                  <option>EVIDÊNCIA</option>
                  <option>OUTRO</option>
                </select>
              </Field>
              <Field label="Número">
                <input name="number" />
              </Field>
              <Field label="Data de emissão">
                <DatePicker name="issuedAt" aria-label="Data de emissão" />
              </Field>
              <Field label="Validade">
                <DatePicker name="expiresAt" aria-label="Validade" />
              </Field>
              <Field label="Alertar com antecedência">
                <input
                  name="alertValue"
                  type="number"
                  min="1"
                  max="3650"
                  placeholder="Ex.: 30"
                />
              </Field>
              <Field label="Unidade do alerta">
                <select name="alertUnit">
                  <option value="days">Dias</option>
                  <option value="months">Meses</option>
                </select>
              </Field>
              <Field label="Situação">
                <select name="status">
                  <option>VIGENTE</option>
                  <option>A VENCER</option>
                  <option>VENCIDO</option>
                  <option>SUBSTITUÍDO</option>
                  <option>ARQUIVADO</option>
                </select>
              </Field>
              <Field label="Responsável">
                <input name="owner" />
              </Field>
              <Field label="Observações" span={2}>
                <textarea name="notes" rows={2} />
              </Field>
              <CertificationAttachmentPicker
                label="Anexos complementares"
                files={attachments}
                onChange={setAttachments}
                onError={onError}
              />
            </div>
            <div className="actions">
              <button
                className="button"
                type="button"
                disabled={saving}
                onClick={() => setCreating(false)}
              >
                Cancelar
              </button>
              <button
                className="button primary"
                type="submit"
                disabled={saving}
              >
                {saving ? "Anexando…" : "Anexar documento"}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {preview && (
        <div
          className="wizard-preview-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPreview(undefined);
          }}
        >
          <section
            className="wizard-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Pré-visualização de ${preview.name}`}
          >
            <header>
              <div>
                <span className="eyebrow">Pré-visualização</span>
                <h3>{preview.name}</h3>
                <small>{Math.max(1, Math.round(preview.size / 1024))} KB</small>
              </div>
              <button
                type="button"
                onClick={() => setPreview(undefined)}
                aria-label="Fechar pré-visualização"
              >
                <X />
              </button>
            </header>
            <div className="wizard-preview-content">
              <AttachmentFullPreview file={preview} />
            </div>
            <footer>
              <a
                className="button wizard-preview-download"
                href={downloadUrl}
                download={preview.name}
                aria-label="Baixar arquivo original"
                title="Baixar arquivo original"
              >
                <Download />
              </a>
            </footer>
          </section>
        </div>
      )}
    </ModuleList>
  );
}

function ModuleList({
  title,
  query,
  onQuery,
  onAdd,
  addLabel,
  className = "",
  view = "table",
  filters,
  controls,
  children,
}: {
  title: string;
  query: string;
  onQuery: (x: string) => void;
  onAdd?: () => void;
  addLabel?: string;
  className?: string;
  view?: "table" | "kanban";
  filters?: React.ReactNode;
  controls?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`card certification-workflow-list data-table-card ${className}`}
    >
      <div className="toolbar table-toolbar">
        <div className="table-filter-group">
          <label className="search">
            <Search />
            <input
              aria-label={`Buscar em ${title}`}
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder="Buscar por código, nome ou responsável"
            />
          </label>
          {filters}
        </div>
        <div className="table-toolbar-actions">
          {controls}
          {onAdd && (
            <button className="button primary" onClick={onAdd}>
              <Plus />
              {addLabel}
            </button>
          )}
        </div>
      </div>
      {view === "table" ? <div className="table-wrap">{children}</div> : children}
    </section>
  );
}
function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: any;
}) {
  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section className="module-dialog" role="dialog" aria-modal="true">
        <header>
          <h2>{title}</h2>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
function Field({
  label,
  wide,
  span,
  children,
}: {
  label: string;
  wide?: boolean;
  span?: 2 | 3 | 4;
  children: any;
}) {
  return (
    <label className={wide ? "wide" : span ? `span-${span}` : ""}>
      <span>{label}</span>
      {children}
    </label>
  );
}
function Actions({ saving = false }: { saving?: boolean }) {
  return (
    <div className="actions">
      <button className="button primary" type="submit" disabled={saving}>
        {saving ? "Salvando…" : "Salvar"}
      </button>
    </div>
  );
}
function Empty({ text }: { text: string }) {
  return (
    <div className="empty compact">
      <Archive />
      <p>{text}</p>
    </div>
  );
}
